import { useEffect, useRef, useState } from 'react';
import { ChromePicker, ColorResult } from 'react-color';
import { RotateCcw } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  DASHBOARD_THEME_FIELDS,
  hasThemeOverrides,
  type DashboardThemeField,
} from '@/lib/dashboardTheme';
import type { DashboardTheme } from '@/services/dashboard.service';

interface ThemeColorFieldProps {
  field: DashboardThemeField;
  /** Which column this sits in, so the picker opens inward instead of off-sheet. */
  align: 'left' | 'right';
  /** The color set at this tier, or undefined when it inherits. */
  value: string | null | undefined;
  /** What the viewer actually sees when `value` is unset, for the swatch. */
  inherited: string;
  onChange: (value: string | null) => void;
}

/**
 * One color. An unset field is not a color — it is "inherit" — so the swatch
 * shows what is inherited while the control reads "Inherited", and the only way
 * back to inheriting is the reset button. Picking a color that happens to equal
 * the inherited one would otherwise pin it, silently breaking the link to the
 * tier above.
 */
function ThemeColorField({ field, align, value, inherited, onChange }: ThemeColorFieldProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isSet = !!value;
  const swatch = value || inherited;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{field.label}</Label>
        {isSet && (
          <button
            type="button"
            onClick={() => onChange(null)}
            title="Inherit this color again"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        )}
      </div>
      <div className="relative">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md border border-input px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors"
          onClick={() => setOpen(!open)}
        >
          <div
            className="w-6 h-6 rounded border border-border flex-shrink-0"
            style={{ backgroundColor: swatch }}
          />
          <span className={`font-mono text-xs ${isSet ? '' : 'text-muted-foreground italic'}`}>
            {isSet ? value : 'Inherited'}
          </span>
        </button>
        {open && (
          <div
            ref={popoverRef}
            className={`absolute z-50 mt-2 ${align === 'right' ? 'right-0' : 'left-0'}`}
          >
            <ChromePicker
              color={swatch}
              onChange={(c: ColorResult) => onChange(c.hex)}
              disableAlpha
            />
          </div>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{field.hint}</p>
    </div>
  );
}

interface ThemeEditorProps {
  theme: DashboardTheme | null | undefined;
  onChange: (theme: DashboardTheme | null) => void;
  /** A panel cannot set the page behind it, so its field list is shorter. */
  scope: 'dashboard' | 'component';
  /**
   * The colors in force without this tier's overrides, used for the swatches.
   * For a component that is the dashboard's resolved theme; for a dashboard it
   * is the tenant's whitelabeling.
   */
  inherited: Record<string, string>;
}

export function ThemeEditor({ theme, onChange, scope, inherited }: ThemeEditorProps) {
  const fields = DASHBOARD_THEME_FIELDS.filter(
    (f) => scope === 'dashboard' || f.scope === 'both',
  );

  const setField = (key: string, value: string | null) => {
    const next: DashboardTheme = { ...(theme || {}) };
    if (value === null) {
      delete (next as Record<string, unknown>)[key];
    } else {
      (next as Record<string, unknown>)[key] = value;
    }
    // An all-empty theme is stored as nothing at all, so a cleared override
    // leaves no trace in the document or in its YAML.
    onChange(hasThemeOverrides(next) ? next : null);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
        {fields.map((field, index) => (
          <ThemeColorField
            key={field.key}
            field={field}
            align={index % 2 === 0 ? 'left' : 'right'}
            value={(theme as Record<string, string | null | undefined> | null | undefined)?.[field.key]}
            inherited={inherited[field.key] || '#ffffff'}
            onChange={(v) => setField(field.key, v)}
          />
        ))}
      </div>
      {hasThemeOverrides(theme) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange(null)}
          className="w-full"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Reset all to inherited
        </Button>
      )}
    </div>
  );
}
